import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Eye,
  EyeOff,
  User,
  Store,
  Mail,
  Lock,
  UserPlus,
  ArrowLeft,
} from "lucide-react";
import { Link } from "react-router-dom";

// Google icon component
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

interface SignUpFormProps {
  onSignUp: (role: "user" | "seller", credentials: any) => void;
  onBackToLogin: () => void;
  defaultTab?: "user" | "seller";
}

export const SignUpForm = ({
  onSignUp,
  onBackToLogin,
  defaultTab = "user",
}: SignUpFormProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [userCredentials, setUserCredentials] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    agreeToTerms: false,
  });
  const [sellerCredentials, setSellerCredentials] = useState({
    businessName: "",
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    agreeToTerms: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleUserSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (userCredentials.password !== userCredentials.confirmPassword) {
      toast({
        title: "Error",
        description: "Passwords do not match",
        variant: "destructive",
      });
      return;
    }
    if (!userCredentials.agreeToTerms) {
      toast({
        title: "Error",
        description: "Please agree to the terms and conditions",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const fullName = `${userCredentials.firstName} ${userCredentials.lastName}`;
      const { data, error } = await supabase.auth.signUp({
        email: userCredentials.email,
        password: userCredentials.password,
        options: {
          data: {
            full_name: fullName,
            role: 'customer',
          },
          emailRedirectTo: `${window.location.origin}/`,
        },
      });

      if (error) throw error;

      toast({
        title: "Success!",
        description: "Account created successfully. Redirecting...",
      });

      onSignUp("user", userCredentials);
    } catch (error: any) {
      toast({
        title: "Sign Up Failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSellerSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sellerCredentials.password !== sellerCredentials.confirmPassword) {
      toast({
        title: "Error",
        description: "Passwords do not match",
        variant: "destructive",
      });
      return;
    }
    if (!sellerCredentials.agreeToTerms) {
      toast({
        title: "Error",
        description: "Please agree to the terms and conditions",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const fullName = `${sellerCredentials.firstName} ${sellerCredentials.lastName}`;
      const { data, error } = await supabase.auth.signUp({
        email: sellerCredentials.email,
        password: sellerCredentials.password,
        options: {
          data: {
            full_name: fullName,
            business_name: sellerCredentials.businessName,
            role: 'weaver',
          },
          emailRedirectTo: `${window.location.origin}/seller/dashboard`,
        },
      });

      if (error) throw error;

      toast({
        title: "Success!",
        description: "Weaver account created successfully. Redirecting to dashboard...",
      });

      onSignUp("seller", sellerCredentials);
    } catch (error: any) {
      toast({
        title: "Sign Up Failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = async (role: "user" | "seller") => {
    try {
      const redirectTo = role === "seller" 
        ? `${window.location.origin}/seller/dashboard`
        : `${window.location.origin}/`;

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });

      if (error) throw error;
    } catch (error: any) {
      toast({
        title: "Google Sign In Failed",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/30 to-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back to Home Button */}
        <div className="mb-6">
          <Link to="/">
            <Button
              variant="ghost"
              size="sm"
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
          </Link>
        </div>

        {/* Logo/Brand */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
            Kente Weave Design
          </h1>
          <p className="text-muted-foreground mt-2">
            Join our community of Kente enthusiasts
          </p>
        </div>

        <Card className="shadow-lg border-border/50">
          <CardHeader className="text-center">
            <div className="flex items-center justify-between mb-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={onBackToLogin}
                className="p-2"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <CardTitle className="text-2xl">Create Account</CardTitle>
              <div className="w-10" /> {/* Spacer for centering */}
            </div>
            <CardDescription>
              Sign up to start creating beautiful Kente designs
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue={defaultTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="user" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Customer
                </TabsTrigger>
                <TabsTrigger value="seller" className="flex items-center gap-2">
                  <Store className="h-4 w-4" />
                  Weaver
                </TabsTrigger>
              </TabsList>

              {/* Customer Sign Up */}
              <TabsContent value="user" className="space-y-4">
                <form onSubmit={handleUserSignUp} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="user-first-name">First Name</Label>
                      <Input
                        id="user-first-name"
                        type="text"
                        placeholder="John"
                        value={userCredentials.firstName}
                        onChange={(e) =>
                          setUserCredentials({
                            ...userCredentials,
                            firstName: e.target.value,
                          })
                        }
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="user-last-name">Last Name</Label>
                      <Input
                        id="user-last-name"
                        type="text"
                        placeholder="Doe"
                        value={userCredentials.lastName}
                        onChange={(e) =>
                          setUserCredentials({
                            ...userCredentials,
                            lastName: e.target.value,
                          })
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="user-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="user-email"
                        type="email"
                        placeholder="john@example.com"
                        value={userCredentials.email}
                        onChange={(e) =>
                          setUserCredentials({
                            ...userCredentials,
                            email: e.target.value,
                          })
                        }
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="user-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="user-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Create a strong password"
                        value={userCredentials.password}
                        onChange={(e) =>
                          setUserCredentials({
                            ...userCredentials,
                            password: e.target.value,
                          })
                        }
                        className="pl-10 pr-10"
                        required
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="user-confirm-password">
                      Confirm Password
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="user-confirm-password"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm your password"
                        value={userCredentials.confirmPassword}
                        onChange={(e) =>
                          setUserCredentials({
                            ...userCredentials,
                            confirmPassword: e.target.value,
                          })
                        }
                        className="pl-10 pr-10"
                        required
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="user-terms"
                      checked={userCredentials.agreeToTerms}
                      onChange={(e) =>
                        setUserCredentials({
                          ...userCredentials,
                          agreeToTerms: e.target.checked,
                        })
                      }
                      className="rounded border-gray-300"
                      aria-label="Agree to terms and conditions"
                    />
                    <Label htmlFor="user-terms" className="text-sm">
                      I agree to the{" "}
                      <Button variant="link" className="p-0 h-auto text-sm">
                        Terms of Service
                      </Button>{" "}
                      and{" "}
                      <Button variant="link" className="p-0 h-auto text-sm">
                        Privacy Policy
                      </Button>
                    </Label>
                  </div>
                  <Button type="submit" className="w-full" size="lg">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Create Customer Account
                  </Button>
                </form>

                {/* Divider */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">
                      Or continue with
                    </span>
                  </div>
                </div>

                {/* Google Sign Up */}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  size="lg"
                  onClick={() => handleGoogleSignUp("user")}
                >
                  <GoogleIcon />
                  <span className="ml-2">Sign up with Google</span>
                </Button>

                <div className="text-center mt-4">
                  <p className="text-sm text-muted-foreground">
                    Already have an account?{" "}
                    <Button
                      variant="link"
                      className="p-0 h-auto"
                      onClick={onBackToLogin}
                    >
                      Sign in
                    </Button>
                  </p>
                </div>
              </TabsContent>

              {/* Weaver Sign Up */}
              <TabsContent value="seller" className="space-y-4">
                <form onSubmit={handleSellerSignUp} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="business-name">Business Name</Label>
                    <Input
                      id="business-name"
                      type="text"
                      placeholder="Your Business Name"
                      value={sellerCredentials.businessName}
                      onChange={(e) =>
                        setSellerCredentials({
                          ...sellerCredentials,
                          businessName: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="seller-first-name">First Name</Label>
                      <Input
                        id="seller-first-name"
                        type="text"
                        placeholder="John"
                        value={sellerCredentials.firstName}
                        onChange={(e) =>
                          setSellerCredentials({
                            ...sellerCredentials,
                            firstName: e.target.value,
                          })
                        }
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="seller-last-name">Last Name</Label>
                      <Input
                        id="seller-last-name"
                        type="text"
                        placeholder="Doe"
                        value={sellerCredentials.lastName}
                        onChange={(e) =>
                          setSellerCredentials({
                            ...sellerCredentials,
                            lastName: e.target.value,
                          })
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="seller-email">Business Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="seller-email"
                        type="email"
                        placeholder="business@example.com"
                        value={sellerCredentials.email}
                        onChange={(e) =>
                          setSellerCredentials({
                            ...sellerCredentials,
                            email: e.target.value,
                          })
                        }
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="seller-phone">Phone Number</Label>
                    <Input
                      id="seller-phone"
                      type="tel"
                      placeholder="+233 24 123 4567"
                      value={sellerCredentials.phone}
                      onChange={(e) =>
                        setSellerCredentials({
                          ...sellerCredentials,
                          phone: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="seller-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="seller-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Create a strong password"
                        value={sellerCredentials.password}
                        onChange={(e) =>
                          setSellerCredentials({
                            ...sellerCredentials,
                            password: e.target.value,
                          })
                        }
                        className="pl-10 pr-10"
                        required
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="seller-confirm-password">
                      Confirm Password
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="seller-confirm-password"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm your password"
                        value={sellerCredentials.confirmPassword}
                        onChange={(e) =>
                          setSellerCredentials({
                            ...sellerCredentials,
                            confirmPassword: e.target.value,
                          })
                        }
                        className="pl-10 pr-10"
                        required
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="seller-terms"
                      checked={sellerCredentials.agreeToTerms}
                      onChange={(e) =>
                        setSellerCredentials({
                          ...sellerCredentials,
                          agreeToTerms: e.target.checked,
                        })
                      }
                      className="rounded border-gray-300"
                      aria-label="Agree to terms and conditions"
                    />
                    <Label htmlFor="seller-terms" className="text-sm">
                      I agree to the{" "}
                      <Button variant="link" className="p-0 h-auto text-sm">
                        Terms of Service
                      </Button>{" "}
                      and{" "}
                      <Button variant="link" className="p-0 h-auto text-sm">
                        Privacy Policy
                      </Button>
                    </Label>
                  </div>
                  <Button type="submit" className="w-full" size="lg">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Create Weaver Account
                  </Button>
                </form>

                {/* Divider */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">
                      Or continue with
                    </span>
                  </div>
                </div>

                {/* Google Sign Up */}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  size="lg"
                  onClick={() => handleGoogleSignUp("seller")}
                >
                  <GoogleIcon />
                  <span className="ml-2">Sign up with Google</span>
                </Button>

                <div className="text-center mt-4">
                  <p className="text-sm text-muted-foreground">
                    Already have an account?{" "}
                    <Button
                      variant="link"
                      className="p-0 h-auto"
                      onClick={onBackToLogin}
                    >
                      Sign in
                    </Button>
                  </p>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-muted-foreground">
          <p>© 2025 Kente Weave Design. Built by Orcta Technologies.</p>
        </div>
      </div>
    </div>
  );
};
